import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { branch, compose, lifecycle, renderComponent, withHandlers, withProps, withStateHandlers } from "recompose";
import { bindActionCreators } from 'redux';
import { cpdPointStatus, defaultTake, routes, userRoles } from '../../../../constants';
import { fullName, getPriorityRole, isBusinessAdminOnly, isExternalUser, isSuperAdmin, isSystemAdmin } from '../../../../services';
import { adminActions } from '../../../../state/modules/admin';
import { ConfirmationModal, LoadingButton, SearchForm, SelectFormField, ServerError } from '../../../components';
import CalculatingLoading from '../../calculating-loading';
import { DataTable } from './data-table';
import DownloadActivityButton from './partial/download-activity-button';
import { UserSnapshotTable } from './partial/user-snapshot-table';
import CopyUserCpdActivityModal from './partial/copy-user-cpd-activity-modal';
import { currentUserSelector } from '../../../../state/modules/user';

const businessUserColumns = [
  {
    columnName: 'business',
    display: () => 'Business'
  },
  {
    columnName: 'firstName',
    display: () => 'First name'
  },
  {
    columnName: 'lastName',
    display: () => 'Last name'
  },
  {
    columnName: 'email',
    display: () => 'Email'
  },
  {
    columnName: 'role',
    display: () => 'Role'
  },
  {
    columnName: 'industry',
    display: () => 'Industry'
  },
  {
    columnName: 'subscriptionStatus',
    display: () => 'Subscription status'
  },
  {
    columnName: 'userStatus',
    display: () => 'User status'
  },
  {
    columnName: 'options',
    display: () => 'Options'
  }
]

const getDownloadReportButton = (label, userId, { yearsFromNow }) => (
  <LinkContainer to={routes.adminPortal.userReport(userId, yearsFromNow)}>
    <Button bsStyle='plain'>
      {label}
    </Button>
  </LinkContainer>
)

const mapStateToProps = (state) => ({
  adminToken: state.session.idToken,
  currentUser: currentUserSelector(state),
  isDisassociatingUserRole: state.admin.isDisassociatingUserRole,
  isAssigningUserRole: state.admin.isAssigningUserRole,
  isFetchingBusinessUser: state.admin.isFetchingBusinessUser,
  businessUserData: state.admin.businessUserData,
  isRemovingUserQuiz: state.admin.isRemovingUserQuiz,
  industries: state.industry.industries,
  isTakingSnapshot: state.admin.isTakingSnapshot,
  isDeactivatingUser: state.admin.isDeactivatingUser,
  usersInBusiness: state.admin.usersInBusiness,
  isDeletingUser: state.admin.isDeletingUser,
  isCopyingActivity: state.admin.isCopyingActivity,
  error: state.admin.error,
})
const mapDispatchToProps = (dispatch) => ({
  getBusinessUsers: bindActionCreators(adminActions.admin.getBusinessUsers, dispatch),
  removeFailedUserQuiz: bindActionCreators(adminActions.admin.removeFailedUserQuiz, dispatch),
  deleteUser: bindActionCreators(adminActions.admin.deleteUser, dispatch),
  deactivateUser: bindActionCreators(adminActions.admin.deactivateUser, dispatch),
  deactivateBusinessUser: bindActionCreators(adminActions.admin.deactivateBusinessUser, dispatch),
  deactivateBusiness: bindActionCreators(adminActions.admin.deactivateBusiness, dispatch),
  snapshotUserTrainingPlan: bindActionCreators(adminActions.admin.snapshotUserTrainingPlan, dispatch),
  getUsersInBusiness: bindActionCreators(adminActions.admin.getUsersInBusiness, dispatch),
  assignUserRole: bindActionCreators(adminActions.admin.assignUserRole, dispatch),
  disassociateUserRole: bindActionCreators(adminActions.admin.disassociateUserRole, dispatch)
})

export const BusinessUserTable = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(
    ({ industries }) => ({
      searchDebounce: undefined,
      fetchBusinessUsersQuery: {
        skip: 0,
        take: defaultTake,
        tags: '',
        industryIds: fp.map('id')(industries).join(',')
      },
      showUserDeletionModal: null,
    }),
    {
      setSelectedIndustry: ({ fetchBusinessUsersQuery }) => (industryIds) => ({
        fetchBusinessUsersQuery: {
          ...fetchBusinessUsersQuery,
          industryIds
        }
      }),
      updateSearchDebounce: () => (searchDebounce) => ({ searchDebounce }),
      onSearchUsers: ({ fetchBusinessUsersQuery }) => (e) => ({
        fetchBusinessUsersQuery: {
          ...fetchBusinessUsersQuery,
          skip: 0,
          tags: e.target.value
        }
      }),
      onLoadMoreUsers: ({ fetchBusinessUsersQuery }) => () => ({
        fetchBusinessUsersQuery: {
          ...fetchBusinessUsersQuery,
          skip: fetchBusinessUsersQuery.skip + defaultTake
        }
      }),
      updateBusinessQuery: ({ fetchBusinessUsersQuery }, { industries }) => (businessQuery) => ({
        fetchBusinessUsersQuery: {
          ...fetchBusinessUsersQuery,
          skip: 0,
          tags: fp.get('email')(businessQuery),
          industryIds: fp.map('id')(industries).join(',')
        }
      }),
      toggleDeletionModal: ({ showUserDeletionModal }) => (userId) => ({
        showUserDeletionModal: showUserDeletionModal === userId ? null : userId
      }),
      setUpdatingUserRole: () => (updatingUserRole) => ({
        updatingUserRole
      })
    }
  ),
  withHandlers({
    handleSearch: ({ onSearchUsers, setBusinessQuery }) => (e) => {
      setBusinessQuery(null)
      onSearchUsers(e)
    }
  }),
  lifecycle({
    componentDidMount() {
      const {
        fetchBusinessUsersQuery,
        getBusinessUsers
      } = this.props
      getBusinessUsers(fetchBusinessUsersQuery)
    },
    componentDidUpdate(prevProps) {
      const {
        fetchBusinessUsersQuery,
        getBusinessUsers,
        searchDebounce,
        updateSearchDebounce,
        businessQuery,
        updateBusinessQuery,
        isFetchingBusinessUser,
        setUpdatingUserRole,
        isAssigningUserRole,
        isDisassociatingUserRole,
        isCopyingActivity,
        error,
      } = this.props;

      const userRoleUpdated = (prevProps.isAssigningUserRole && !isAssigningUserRole) || (prevProps.isDisassociatingUserRole && !isDisassociatingUserRole);

      // When we start updating user role, update the updating status.
      if (isAssigningUserRole || isDisassociatingUserRole) {
        setUpdatingUserRole(true);
      }

      // Re-fetch the user's detail after the role has been updated.
      // So that the user can see feedback on screen.
      if (userRoleUpdated) {
        getBusinessUsers(fetchBusinessUsersQuery);
      }

      // Re-fetch the user's detail after the cpd activity was copied.
      if (!isCopyingActivity && prevProps.isCopyingActivity && error === null) {
        getBusinessUsers({
          ...fetchBusinessUsersQuery,
          take: fetchBusinessUsersQuery.skip + fetchBusinessUsersQuery.take,
          skip: 0,
        });
      }

      // Set not updating user role after the business users has been fetched.
      // Because a user role update will always follow a business users fetch here.
      if (!isFetchingBusinessUser && prevProps.isFetchingBusinessUser) {
        setUpdatingUserRole(false);
      }

      if (!fp.isEqual(prevProps.fetchBusinessUsersQuery, fetchBusinessUsersQuery)) {
        const debounced = fp.debounce(500)(
          () => {
            getBusinessUsers(fetchBusinessUsersQuery)
          }
        );
        updateSearchDebounce(debounced);
        searchDebounce && searchDebounce.cancel();
        debounced();
      }

      if (businessQuery !== prevProps.businessQuery) {
        updateBusinessQuery(businessQuery)
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
      }
    }
  }),
  branch(
    ({ businessUserData }) => !businessUserData,
    renderComponent(CalculatingLoading)
  ),
  withProps(
    ({ businessUserData }) => ({
      hasMore: businessUserData.result.length < businessUserData.hits
    })
  ),
  withStateHandlers(
    {
      showUserDetailModal: null,
      showUserDeactivationModal: null,
      showCopyCpdActivityModal: null,
    },
    {
      toggleDetailsModal: ({ showUserDetailModal }) => (userId) => ({
        showUserDetailModal: showUserDetailModal === userId ? null : userId
      }),
      toggleDeactivationModal: ({showUserDeactivationModal}) => (userId) => ({
        showUserDeactivationModal: showUserDeactivationModal === userId ? null : userId
      }),
      toggleCopyCpdActivityModal: ({ showCopyCpdActivityModal }) => (userId) => ({
        showCopyCpdActivityModal: showCopyCpdActivityModal === userId ? null : userId
      }),
    }
  ),
  withProps(
    ({
      businessUserData,
      authenticate,
      adminToken,
      removeFailedUserQuiz,
      isRemovingUserQuiz,
      toggleDetailsModal,
      showUserDetailModal,
      isTakingSnapshot,
      snapshotUserTrainingPlan,
      isDeactivatingUser,
      deactivateUser,
      deactivateBusiness,
      deactivateBusinessUser,
      getUsersInBusiness,
      showUserDeactivationModal,
      toggleDeactivationModal,
      usersInBusiness,
      deleteUser,
      isDeletingUser,
      showUserDeletionModal,
      toggleDeletionModal,
      assignUserRole,
      disassociateUserRole,
      isDisassociatingUserRole,
      isAssigningUserRole,
      currentUser,
      updatingUserRole,
      isCopyingActivity,
      showCopyCpdActivityModal,
      toggleCopyCpdActivityModal,
      error,
    }) => ({
      rows: fp.flow(
        fp.get('result'),
        fp.map(
          ({ user, roles, businesses, trainingPlans, snapshots, subscription, cpdPoints }) => {
            const industry = fp.flow(
              fp.map(
                fp.get('industry.name')
              ),
              (names) => names.join(', ')
            )(trainingPlans);

            const failedCpdPoints = fp.flow(
              fp.filter(
                ({ status }) => status === cpdPointStatus.denied
              ),
              fp.get('length')
            )(cpdPoints);

            const business = fp.flow(
              fp.map('name'),
              (names) => names.join(', ')
            )(businesses);

            const businessId = fp.flow(
              fp.first,
              fp.get('id'),
            )(businesses);

            const userRole = { roleAssignments: roles };
            const isActive = user.activated;
            const isExternal = isExternalUser(userRole);
            const isBusinessAdmin = isBusinessAdminOnly(userRole);
            const isCurrentUserSuperAdmin = isSuperAdmin(currentUser);
            const isUserSystemAdmin = isSystemAdmin(userRole);
            const isUserSuperAdmin = isSuperAdmin(userRole);

            const isCompliiUser = industry === "Complii";
            const onlyUserInBusiness = usersInBusiness !== undefined && usersInBusiness.length === 1;
            const businessAdmins = usersInBusiness !== undefined && usersInBusiness.filter(user => user.roles.find(roleAssignment => roleAssignment.role === userRoles.BusinessAdmin));

            const priorityRole = getPriorityRole(roles);
            const priorityRoleName = priorityRole === undefined ? "" : priorityRole.name;
            
            // Only allow the business user deactivation if 
            // - if this is not a Complii user, or
            // - if this is a Complii user, and 
            //   - there are more than one Business Admin in the business, or
            //   - the user is not a business admin, or 
            //   - this is the only user in the business, or
            const allowDeactivate = (
              isCompliiUser && (businessAdmins.length > 1 || !isBusinessAdmin || onlyUserInBusiness)
            ) || !isCompliiUser;
            
            const userDeactivationMessage = (userSubscription, allowDeactivation) => {
              if (!allowDeactivation) {
                return (
                  <div>
                    <p>This user is the only Business Admin of {business} and there are other users in the business, please promote another Business Admin before deactivating this user.</p>
                  </div>
                );
              }

              if (userSubscription) {
                return (
                  <div>
                    <p>This is the user that hold the subscription of business {business}.</p>
                    <p>Deactivate this user will:</p>
                    <ul style={{ textAlign: 'left' }}>
                      <li>Set all the users in this business as inactive</li>
                      <li>Cancel the business subscription</li>
                    </ul>
                    <p>Are you sure you want to deactivate the whole business?</p>
                  </div>
                );
              }

              return (
                <div>
                  <p>This is an {isExternal ? 'External' : 'Caddie'} user of business {business}.</p>
                  <p>Deactivate this user will:</p>
                  <ul style={{ textAlign: 'left' }}>
                    <li>Set the user status to inactive</li>
                    <li>Remove the user from its business</li>
                  </ul>
                  <p>Are you sure you want to deactivate this user?</p>
                </div>
              )
            }

            return ({
              ...user,
              role: priorityRoleName,
              industry,
              business,
              userStatus: user.activated ? 'Active' : 'Inactive',
              subscriptionStatus: subscription ? subscription.status : "no subscription",
              options: <div>
                <Button onClick={() => toggleDetailsModal(user.id)}>Details</Button>
                <ConfirmationModal
                  className="update-user-details-modal"
                  show={showUserDetailModal === user.id}
                  actions={[]}
                  cancel={() => toggleDetailsModal(user.id)}
                  cancelLabel='Back'
                  message={
                    <form className='top-gap'>
                      <Row>
                        <Col xs={12}>
                          <ServerError error={error} hideSupport />
                        </Col>
                      </Row>
                      <div className='top-gap user-role'>
                        <span className='title'>User Role:</span>
                        <span>{fp.map('role')(roles).join('/')}</span>
                      </div>
                      {
                        // If the current logged in user is a Super Admin and the user details in the modal is not the super admin.
                        isCurrentUserSuperAdmin && !isUserSuperAdmin ? (
                          <div className='top-gap'>
                            <LoadingButton 
                              className={`top-gap-xs ${isUserSystemAdmin ? 'btn-delete' : ''}`}
                              disabled={updatingUserRole}
                              loading={updatingUserRole}
                              onClick={() => 
                                isUserSystemAdmin ?
                                  disassociateUserRole(user.id, userRoles.SystemAdmin) : 
                                  assignUserRole(user.id, userRoles.SystemAdmin)
                              }
                              text={isUserSystemAdmin ? "Remove System Admin Role" : "Assign System Admin Role"}
                            />
                          </div>
                        ) : <div></div>
                      }
                      <div className='top-gap flexbox align-center reports'>
                        <span className='title'>Reports:</span>
                        <span>
                          {
                            getDownloadReportButton('Current Period', user.id, { yearsFromNow: 0 })
                          }
                        </span>
                        <span>
                          {
                            getDownloadReportButton('Last Period', user.id, { yearsFromNow: -1 })
                          }
                        </span>
                        <span>
                          <DownloadActivityButton {...{
                            user,
                            trainingPlans,
                            cpdPoints
                          }} />
                        </span>
                      </div>
                      <div className='top-gap flexbox nowrap'>
                        <span className='title'>Actions:</span>
                        <div className='flexbox'>
                          <LoadingButton className='right-gap' text={`Remove ${failedCpdPoints} Failed Quiz`} loading={isRemovingUserQuiz} onClick={() => removeFailedUserQuiz(user.id)} />
                          <LoadingButton text={`Snapshot Training Plan`} loading={isTakingSnapshot} onClick={() => snapshotUserTrainingPlan(user.id)} />
                          {
                            isExternal ? 
                              <LoadingButton
                                className='top-gap-xs right-gap'
                                text={`Transfer user activities`}
                                loading={isCopyingActivity}
                                onClick={() => toggleCopyCpdActivityModal(user.id)}
                              /> :
                              null
                          }
                          {
                            isActive ?
                              <LoadingButton
                                className='top-gap-xs btn-delete'
                                text={`Deactivate`}
                                loading={isDeactivatingUser}
                                onClick={() =>toggleDeactivationModal(user.id)}
                              /> :
                              null
                          }
                          {
                            !user.activated ?
                              <LoadingButton
                                className='top-gap-xs btn-delete'
                                text={`Delete`}
                                loading={isDeletingUser}
                                onClick={() => toggleDeletionModal(user.id)}
                              /> :
                              null
                          }
                        </div>
                      </div>
                      <div className='top-gap training-plan'>
                        <span className='title'>Training Plan:</span>
                        <UserSnapshotTable user={user} snapshots={snapshots} />
                      </div>
                    </form>
                  }
                  headingText={
                    <div className='flexbox justify-between align-center'>
                      <span>{fullName(user)} Details</span>
                      <Button onClick={() => authenticate(adminToken, user.id)}>Login</Button>
                    </div>
                  }
                />
                {
                  showUserDeactivationModal === user.id ? (
                    <ConfirmationModal
                      className="user-deactivation-modal"
                      show={true}
                      cancel={() => toggleDeactivationModal(user.id)}
                      componentDidMountAction={() => getUsersInBusiness(businessId)}
                      actions={[{
                        onConfirmAction: () => {
                          if (isExternal) {
                            deactivateUser(user.id)
                          } else if (subscription) {
                            deactivateBusiness(businessId);
                          } else {
                            deactivateBusinessUser(businessId, user.id);
                          }
                          toggleDeactivationModal(user.id)
                        },
                        actionLabel: 'Yes, deactivate',
                        hideActionButton: !allowDeactivate,
                      }]}
                      cancelLabel='Cancel'
                      headingText={<div>Deactivate {fullName(user)}</div>}
                      message={userDeactivationMessage(subscription, allowDeactivate)}
                    />
                  ) : <div></div>
                }
                <ConfirmationModal
                  className="user-deletion-modal"
                  show={showUserDeletionModal === user.id}
                  cancel={() => toggleDeletionModal(user.id)}
                  actions={[{
                    onConfirmAction: () => {
                      deleteUser(user.id);
                      toggleDeletionModal(user.id);
                      toggleDetailsModal(user.id);
                    },
                    actionLabel: 'Yes, delete',
                  }]}
                  cancelLabel='Cancel'
                  headingText={<div>Delete {fullName(user)}</div>}
                  message={
                    <div>
                      <p>Are you sure you want to delete this user?</p>
                    </div>
                  }
                />
                <CopyUserCpdActivityModal
                  showCopyCpdActivityModal={showCopyCpdActivityModal}
                  toggleCopyCpdActivityModal={toggleCopyCpdActivityModal}
                  user={user}
                />
              </div >
            })
          }
        ),
        fp.flatten
      )(businessUserData),
      columns: businessUserColumns
    })
  )
)(
  ({ handleSearch, fetchBusinessUsersQuery, isFetchingBusinessUser, hasMore, onLoadMoreUsers, setSelectedIndustry, industries, businessQuery, ...props }) => {
    return <div>
      <Row className='no-gutter top-gap'>
        {
          businessQuery ?
            <Col sm={12}>
              <h2>Showing all business users for {fp.get('name')(businessQuery)}</h2>
            </Col> : null
        }
        <Col md={3} sm={12}>
          <SearchForm 
            label="Search business users"
            searchQuery={fetchBusinessUsersQuery.tags}
            onSearchChange={handleSearch}
          />
        </Col>
        <Col md={3} sm={12} mdOffset={1}>
          <form>
            <SelectFormField
              emptyOption={true}
              label='Select industry'
              onChange={(e) => {
                const industryId = parseInt(e.target.value, 10)
                if (!industryId) {
                  const allIndustryIds = fp.map('id')(industries).join(',')
                  setSelectedIndustry(allIndustryIds)
                } else {
                  setSelectedIndustry(industryId)
                }
              }}>
              {
                fp.map(
                  ({ id, name }) => <option key={id} value={id}>{he.decode(name)}</option>
                )(industries)
              }
            </SelectFormField>
          </form>
        </Col>
      </Row>
      <DataTable {...{...props, isLoading: isFetchingBusinessUser}} />
      {
        hasMore ?
          <LoadingButton text='Load More' loading={isFetchingBusinessUser} onClick={onLoadMoreUsers} /> :
          null
      }
    </div>
  }
)


// WEBPACK FOOTER //
// ./src/containers/pages/admin-portal/user-portal/business-user-table.js